import { useEffect, useState } from "react";
import { Loader2, RefreshCw, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { adminListLeads, adminUpdateLead } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const STATUS_COLOR = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-sky-100 text-sky-800 border-sky-200",
  scheduled: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function AdminLeads() {
  useSeo({ title: "Admin · Leads", description: "Tampa Bay Pool Pros admin leads dashboard.", path: "/admin/leads" });

  const [token, setToken] = useState(() => localStorage.getItem("tbpp_admin_token") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeads = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const data = await adminListLeads(t);
      setLeads(data);
    } catch (err) {
      toast.error("Could not load leads. Token may be invalid.");
      setToken("");
      localStorage.removeItem("tbpp_admin_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    localStorage.setItem("tbpp_admin_token", input.trim());
    setToken(input.trim());
  };

  const logout = () => {
    localStorage.removeItem("tbpp_admin_token");
    setToken("");
    setLeads([]);
  };

  const updateStatus = async (id, status) => {
    try {
      const updated = await adminUpdateLead(token, id, status);
      setLeads((arr) => arr.map((l) => (l.id === id ? updated : l)));
      toast.success("Status updated.");
    } catch (err) {
      toast.error("Failed to update.");
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-amber-50/40">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl bg-white border border-slate-100 shadow-xl p-8"
          data-testid="admin-login-form"
        >
          <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Sign-in</h1>
          <p className="mt-1 text-sm text-slate-600">Enter your admin token to view leads.</p>
          <div className="mt-5">
            <Label htmlFor="admin-token">Admin token</Label>
            <Input
              id="admin-token"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="mt-1.5"
              data-testid="admin-token-input"
              placeholder="Paste token"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            className="mt-5 w-full rounded-full bg-slate-900 hover:bg-sky-700"
            data-testid="admin-login-submit"
          >
            Sign in
          </Button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 py-24 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Leads</h1>
            <p className="text-sm text-slate-600">{leads.length} total inquiries</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fetchLeads()}
              disabled={loading}
              data-testid="admin-refresh"
              className="rounded-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>

        <div className="mt-6 rounded-3xl bg-white border border-slate-100 shadow-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Email sent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                    No leads yet.
                  </TableCell>
                </TableRow>
              )}
              {leads.map((l) => (
                <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                  <TableCell className="text-xs whitespace-nowrap text-slate-500">
                    {new Date(l.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-semibold">{l.name}</TableCell>
                  <TableCell>
                    <a href={`tel:${l.phone}`} className="text-sky-700 hover:underline">
                      {l.phone}
                    </a>
                  </TableCell>
                  <TableCell>{l.city}</TableCell>
                  <TableCell>{l.service}</TableCell>
                  <TableCell>
                    <Badge variant={l.email_sent ? "default" : "secondary"}>
                      {l.email_sent ? "Sent" : "Stored only"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger
                        className={`h-8 text-xs border ${STATUS_COLOR[l.status]}`}
                        data-testid={`admin-lead-status-${l.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="max-w-[260px] text-xs text-slate-600">
                    {l.message || "—"}
                    {l.email && (
                      <div className="text-slate-400">{l.email}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </main>
  );
}
